import type {Media, WireMediaRef} from './media';
import type {WireNewsCard} from './news';

export enum ScreenSectionType {
  Hero = 'HERO',
  Spotlight = 'SPOTLIGHT',
  FeaturedProjects = 'FEATURED_PROJECTS',
  Countries = 'COUNTRIES',
  AboutTeaser = 'ABOUT_TEASER',
  LatestNews = 'LATEST_NEWS',
  Quote = 'QUOTE',
  Overview = 'OVERVIEW',
  OverviewAndStatistics = 'OVERVIEW_AND_STATISTICS',
  FeatureItems = 'FEATURE_ITEMS',
  MediaBanner = 'MEDIA_BANNER',
}

export enum SpotlightRedirectKind {
  Country = 'COUNTRY',
  Project = 'PROJECT',
  Phase = 'PHASE',
  Unit = 'UNIT',
}

export interface WireSectionBase {
  uuid: string;
  name?: string;
}

export interface HeroWireSection extends WireSectionBase {
  type: ScreenSectionType.Hero;
  hero?: {webMedia?: WireMediaRef; mobileMedia?: WireMediaRef; registerInterestEnabled?: boolean};
}

export interface WireSpotlightRedirect {
  kind?: SpotlightRedirectKind;
  targetId?: string;
  projectId?: string;
}

export interface WireSpotlightHighlight {
  label?: string;
  value?: string;
}

export interface SpotlightWireSection extends WireSectionBase {
  type: ScreenSectionType.Spotlight;
  spotlight?: {
    label?: string;
    description?: string;
    buttonText?: string;
    redirect?: WireSpotlightRedirect;
    topLeft?: WireMediaRef;
    bottomLeft?: WireMediaRef;
    right?: WireMediaRef;
    highlights?: WireSpotlightHighlight[];
  };
}

export interface FeaturedProjectsWireSection extends WireSectionBase {
  type: ScreenSectionType.FeaturedProjects;
  featuredProjects?: {projects?: {sfId: string; order: number}[]};
}

export interface CountriesWireSection extends WireSectionBase {
  type: ScreenSectionType.Countries;
  countriesSection?: {countries?: {id: string; order: number}[]};
}

export interface AboutTeaserWireSection extends WireSectionBase {
  type: ScreenSectionType.AboutTeaser;
  aboutTeaser?: {
    description?: string;
    leftBackground?: WireMediaRef;
    rightBackground?: WireMediaRef;
    leftCircle?: WireMediaRef;
    rightCircle?: WireMediaRef;
  };
}

export interface LatestNewsWireSection extends WireSectionBase {
  type: ScreenSectionType.LatestNews;
  cards?: WireNewsCard[];
}

export interface QuoteWireSection extends WireSectionBase {
  type: ScreenSectionType.Quote;
  quote?: {media?: WireMediaRef};
}

export interface WireStatistic {
  uuid?: string;
  name: string;
  displayValue: string;
}

export interface OverviewWireSection extends WireSectionBase {
  type: ScreenSectionType.Overview;
  overview?: {displayLabel?: string; subtitle?: string; description?: string; media?: WireMediaRef};
}

export interface OverviewStatsWireSection extends WireSectionBase {
  type: ScreenSectionType.OverviewAndStatistics;
  overview?: {
    name: string;
    displayLabel?: string;
    subtitle?: string;
    description?: string;
    media?: WireMediaRef;
    statistics?: WireStatistic[];
  };
}

export interface WireFeatureItem {
  uuid?: string;
  name: string;
  subTitle: string;
  icon: string;
}

export interface FeatureItemsWireSection extends WireSectionBase {
  type: ScreenSectionType.FeatureItems;
  featureItems?: {
    displayLabel?: string;
    subtitle?: string;
    description?: string;
    media?: WireMediaRef;
    isFeatureItemsVisible?: boolean;
    featureItems?: WireFeatureItem[];
  };
}

export interface MediaBannerWireSection extends WireSectionBase {
  type: ScreenSectionType.MediaBanner;
  mediaBanner?: {webMedia?: WireMediaRef; mobileMedia?: WireMediaRef};
}

export type DetailedScreenSection =
  | HeroWireSection
  | SpotlightWireSection
  | FeaturedProjectsWireSection
  | CountriesWireSection
  | AboutTeaserWireSection
  | LatestNewsWireSection
  | QuoteWireSection
  | OverviewWireSection
  | OverviewStatsWireSection
  | FeatureItemsWireSection
  | MediaBannerWireSection;

export interface GrovaAboutContent {
  bannerTitle: string;
  banner: Media;
  sections: DetailedScreenSection[];
}
